/**
 * RISK CATEGORIES CONFIGURATION
 * 
 * Structured risk categories with mandatory details requirement
 */

export const RISK_CATEGORIES = [
  {
    value: 'access',
    label: 'Access / Logistics',
    icon: '🚚',
    description: 'Parking, entry, material delivery, equipment access',
    examples: [
      'No parking within 100ft',
      'Narrow hallways limit material movement',
      'Third floor walkup',
      'HOA approval required for dumpster',
    ],
  },
  {
    value: 'structural',
    label: 'Structural Concerns',
    icon: '🏗️',
    description: 'Load-bearing walls, foundation, framing issues',
    examples: [
      'Suspected load-bearing wall in demo area',
      'Floor bounce indicates weak joists',
      'Visible foundation cracks',
      'Sagging ceiling may indicate roof issue',
    ],
  },
  {
    value: 'hidden',
    label: 'Hidden Conditions',
    icon: '🔍',
    description: 'Unknown behind walls, under floors, in ceilings',
    examples: [
      'Cannot verify plumbing condition behind wall',
      'Subfloor condition unknown',
      'Electrical may not meet code',
      'Insulation/asbestos unknown',
    ],
  },
  {
    value: 'mechanical',
    label: 'Mechanical / HVAC',
    icon: '🌡️',
    description: 'HVAC, ventilation, ductwork issues',
    examples: [
      'HVAC system undersized for addition',
      'No return air in bathroom',
      'Ductwork may need rerouting',
      'Exhaust fan not vented outside',
    ],
  },
  {
    value: 'electrical',
    label: 'Electrical Issues',
    icon: '⚡',
    description: 'Panel capacity, wiring, code compliance',
    examples: [
      'Panel at capacity',
      'No GFCI in wet areas',
      'Aluminum wiring present',
      'Insufficient outlets for code',
    ],
  },
  {
    value: 'plumbing',
    label: 'Plumbing Issues',
    icon: '🔧',
    description: 'Supply lines, drains, venting, water heater',
    examples: [
      'Galvanized pipes - may fail during work',
      'Drain location requires relocation',
      'Insufficient water pressure',
      'Water heater undersized',
    ],
  },
  {
    value: 'code',
    label: 'Code / Permit',
    icon: '📋',
    description: 'Building codes, permit requirements, inspections',
    examples: [
      'Permit required - adds timeline',
      'Egress window required for bedroom',
      'Smoke/CO detector locations required',
      'Previous work may not be permitted',
    ],
  },
  {
    value: 'material',
    label: 'Material Availability',
    icon: '📦',
    description: 'Lead times, discontinued items, custom orders',
    examples: [
      'Tile is special order - 6 week lead',
      'Matching existing flooring discontinued',
      'Custom cabinet size required',
      'Color match may be difficult',
    ],
  },
  {
    value: 'timeline',
    label: 'Timeline Risk',
    icon: '📅',
    description: 'Schedule conflicts, dependencies, delays',
    examples: [
      'Client has hard move-in date',
      'Work depends on permit approval',
      'Weather-dependent exterior work',
      'Coordination with other trades required',
    ],
  },
  {
    value: 'budget',
    label: 'Budget Concern',
    icon: '💰',
    description: 'Cost overrun potential, allowance issues',
    examples: [
      'Client selections exceed allowance',
      'Hidden conditions likely to add cost',
      'Scope creep potential high',
      'Material costs volatile',
    ],
  },
  {
    value: 'client',
    label: 'Client Expectations',
    icon: '👤',
    description: 'Communication, decision-making, satisfaction risk',
    examples: [
      'Multiple decision-makers with different opinions',
      'Unrealistic timeline expectations',
      'Very particular about details',
      'Previous bad contractor experience',
    ],
  },
  {
    value: 'environmental',
    label: 'Environmental / Hazmat',
    icon: '☢️',
    description: 'Asbestos, lead, mold, water damage',
    examples: [
      'Pre-1978 home - lead paint likely',
      'Popcorn ceiling may contain asbestos',
      'Water stains indicate possible mold',
      'Musty smell in basement',
    ],
  },
  {
    value: 'other',
    label: 'Other',
    icon: '📝',
    description: 'Any risk not covered above',
    examples: [],
  },
];

export const IMPACT_LEVELS = [
  {
    value: 'low',
    label: 'Low',
    color: 'green',
    icon: '🟢',
    description: 'Minor inconvenience, unlikely to affect scope/budget',
  },
  {
    value: 'medium',
    label: 'Medium',
    color: 'amber',
    icon: '🟡',
    description: 'May affect scope, timeline, or budget by 10-20%',
  },
  {
    value: 'high',
    label: 'High',
    color: 'red',
    icon: '🔴',
    description: 'Could significantly impact project or block work',
  },
];

export const RISK_OWNERS = [
  { value: 'contractor', label: 'Contractor', description: 'We own and mitigate this risk' },
  { value: 'client', label: 'Client', description: 'Client is responsible for this' },
  { value: 'shared', label: 'Shared', description: 'Both parties share responsibility' },
];

// Get risk category config by value
export const getRiskCategory = (value) => {
  return RISK_CATEGORIES.find(c => c.value === value) || RISK_CATEGORIES[RISK_CATEGORIES.length - 1];
};

// Get impact level config by value
export const getImpactLevel = (value) => {
  return IMPACT_LEVELS.find(l => l.value === value) || IMPACT_LEVELS[0];
};

export default { RISK_CATEGORIES, IMPACT_LEVELS, RISK_OWNERS, getRiskCategory, getImpactLevel };
